import { useMemo } from 'react';
import { useStore } from 'store/store';
import {
  InstructionQuoteRequestAttributesMethod,
  useUtxInstructionApiPostInstructionQuoteRequest,
  useUtxTransferPeerGetTransferPeers,
  useUtxInstructionApiPostInstructionSubmit,
  useUtxTransferPeerValidateTransferPeer,
  useUtxTransferPeerGetTransferPeerSchemaByType
} from '@utx/utx-client';

export interface useInstructionsQuoteParams {
  intent: string;
  idempotencyKey: string;
  accountId: string;
  sourceCurrency?: string;
  sourceAmount?: number;
  destinationCurrency?: string;
  destinationAmount?: number;
  method: InstructionQuoteRequestAttributesMethod;
  source: any;
}

export interface useInstructionsSubmitParams {
  intent: string;
  instructionId: string;
  amount: string;
  asset: string;
  purpose: string;
  remittancePurpose?: string;
  fundSource?: string;
  feeSnapshotId: string;
  method: InstructionQuoteRequestAttributesMethod;
  accountId: string;
  destination: any;
  token?: string;
  verifyType?: number;
  sms_otp?: string;
  email_otp?: string;
  otp?: string;
}
export const useInstructions = () => {
  const store = useStore();
  const quoteQuery = useUtxInstructionApiPostInstructionQuoteRequest();
  const runQuote = (params: useInstructionsQuoteParams, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    const { intent, idempotencyKey, accountId, sourceCurrency, sourceAmount, destinationCurrency, destinationAmount, method, source } =
      params;
    quoteQuery.mutate(
      {
        data: {
          intent: intent,
          idempotencyKey: idempotencyKey,
          attributes: {
            accountId: accountId,
            sourceAmount: sourceAmount && sourceAmount,
            sourceCurrency: sourceCurrency,
            destinationCurrency: destinationCurrency,
            destinationAmount: destinationAmount && destinationAmount,
            amount: sourceAmount && `${sourceAmount}`,
            asset: sourceCurrency,
            method: method,
            source: source
          }
        },
        axios: {
          headers: {
            Authorization: `Bearer ${store.authToken}`
          }
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const submitQuery = useUtxInstructionApiPostInstructionSubmit();
  const runSubmit = (params: useInstructionsSubmitParams, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    const {
      intent,
      instructionId,
      amount,
      asset,
      purpose,
      remittancePurpose,
      fundSource,
      feeSnapshotId,
      method,
      accountId,
      destination,
      token,
      verifyType,
      sms_otp,
      email_otp,
      otp
    } = params;
    submitQuery.mutate(
      {
        data: {
          intent: intent,
          instructionId: instructionId,
          amount: amount,
          asset: asset,
          purpose: purpose,
          remittancePurpose: remittancePurpose,
          fundSource: fundSource,
          feeSnapshotId: feeSnapshotId,
          method: method,
          accountId: accountId,
          destination: destination,
          token: token,
          verifyType: verifyType,
          sms_otp: sms_otp,
          email_otp: email_otp,
          otp: otp
        },
        axios: {
          headers: {
            Authorization: `Bearer ${store.authToken}`
          }
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  return { runQuote, runSubmit };
};

export interface useTransferPeerParams {
  types: string[];
  current?: number;
  pageSize?: number;
  label?: string;
}

export const useTransferPeer = ({ types, current, pageSize = 10, label }: useTransferPeerParams) => {
  const store = useStore();
  const queryTransferPeers = useUtxTransferPeerGetTransferPeers(
    {
      types: types,
      current: current,
      pageSize: pageSize,
      label: label
    },
    {
      query: {
        queryKey: [store.authToken, types, current, pageSize, label],
        retry: false
      }
    }
  );

  const transferPeers = useMemo(() => {
    const data = queryTransferPeers.data?.data.data;
    return data || [];
  }, [queryTransferPeers.data?.data]);

  const total = useMemo(() => {
    const data = queryTransferPeers.data?.data.total;
    return data || 0;
  }, [queryTransferPeers.data?.data]);

  const isLoading = useMemo(() => {
    return queryTransferPeers.isLoading;
  }, [queryTransferPeers.isLoading]);

  return {
    transferPeers,
    isLoading,
    total
  };
};

export interface useTransferPeerValidateParams {
  transferPeerTypeId: string;
  attributes: any;
  label?: string;
}

export const useTransferPeerValidate = ({ type }: { type: string }) => {
  const store = useStore();

  const queryTransferPeerSchemaByType = useUtxTransferPeerGetTransferPeerSchemaByType(
    {
      type: type
    },
    {
      query: {
        queryKey: [store.authToken, type],
        retry: false
      }
    }
  );

  const transferPeerSchemaByType = useMemo(() => {
    const data = queryTransferPeerSchemaByType.data?.data;
    return data || [];
  }, [queryTransferPeerSchemaByType.data?.data]);

  const transferPeerValidateQuery = useUtxTransferPeerValidateTransferPeer();
  const runTransferPeerValidate = (
    params: useTransferPeerValidateParams,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    const { transferPeerTypeId, attributes, label } = params;
    transferPeerValidateQuery.mutate(
      {
        data: {
          transferPeerTypeId: transferPeerTypeId,
          attributes: attributes,
          label: label
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  return { transferPeerSchemaByType, runTransferPeerValidate };
};
